import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { Alert, AlertDescription } from "../components/ui/alert";
import { 
  ArrowLeft, 
  ArrowRight, 
  Loader2, 
  Target, 
  Lightbulb, 
  User, 
  DollarSign, 
  Megaphone, 
  ClipboardCheck,
  Calendar,
  Sparkles,
  CheckCircle2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  {
    id: "problem",
    title: "The Problem",
    icon: Target,
    description: "Define the problem you're solving",
    fields: [
      { key: "target_customer", label: "Target Customer", placeholder: "Who specifically are you helping?", type: "text" },
      { key: "specific_problem", label: "Specific Problem", placeholder: "What exact problem do they face?", type: "textarea" },
      { key: "moment_of_pain", label: "Moment of Pain", placeholder: "When exactly do they feel this pain?", type: "text" },
      { key: "cost_of_problem", label: "Cost of the Problem", placeholder: "What does this problem cost them? (time, money, stress)", type: "textarea" }
    ]
  },
  {
    id: "solution",
    title: "The Solution",
    icon: Lightbulb,
    description: "Define your solution clearly",
    fields: [
      { key: "one_sentence_solution", label: "One-Sentence Solution", placeholder: "We help [target customer] to [achieve outcome] by [method]", type: "textarea" },
      { key: "top_3_outcomes", label: "Top 3 Outcomes", placeholder: "What are the top 3 results customers will get?", type: "array" },
      { key: "deliberately_excluded", label: "What's Deliberately Excluded", placeholder: "What are you NOT doing? (scope control)", type: "textarea" }
    ]
  },
  {
    id: "founder_fit",
    title: "Founder Fit",
    icon: User,
    description: "Why are you the right person?",
    fields: [
      { key: "founder_advantage", label: "Your Founder Advantage", placeholder: "What unique advantage do you have?", type: "textarea" },
      { key: "why_now", label: "Why Now?", placeholder: "Why is this the right time for this solution?", type: "textarea" }
    ]
  },
  {
    id: "offer",
    title: "Offer & Pricing",
    icon: DollarSign,
    description: "Define your first offer",
    fields: [
      { key: "first_offer", label: "First Offer", placeholder: "What's your minimum viable offer?", type: "textarea" },
      { key: "simple_pricing", label: "Simple Pricing", placeholder: "What's your price point?", type: "text" },
      { key: "what_customer_gets", label: "What the Customer Gets", placeholder: "What exactly do they receive?", type: "textarea" }
    ]
  },
  {
    id: "go_to_market",
    title: "Go-To-Market",
    icon: Megaphone,
    description: "How will you reach customers?",
    fields: [
      { key: "primary_acquisition_channel", label: "Primary Acquisition Channel", placeholder: "Where will you find your first customers?", type: "text" },
      { key: "first_10_customers_plan", label: "First 10 Customers Plan", placeholder: "How will you get your first 10 paying customers?", type: "textarea" },
      { key: "outreach_hook", label: "One-Sentence Outreach Hook", placeholder: "Your compelling opening line for outreach", type: "textarea" }
    ]
  },
  {
    id: "assumptions",
    title: "Assumptions & Tests",
    icon: ClipboardCheck,
    description: "What do you need to validate?",
    fields: [
      { key: "top_3_assumptions", label: "Top 3 Assumptions to Validate", placeholder: "What must be true for this to work?", type: "array" },
      { key: "validation_tests", label: "7-Day Validation Tests", placeholder: "Select tests to run", type: "checklist" },
      { key: "success_criteria", label: "Success Criteria (with numbers)", placeholder: "e.g., 5 paid sign-ups, 20% conversion rate", type: "textarea" }
    ]
  },
  {
    id: "action_plan",
    title: "30-Day Action Plan",
    icon: Calendar,
    description: "Your week-by-week plan",
    fields: [
      { key: "week_1", label: "Week 1", placeholder: "Focus: Problem validation", type: "textarea" },
      { key: "week_2", label: "Week 2", placeholder: "Focus: Solution validation", type: "textarea" },
      { key: "week_3", label: "Week 3", placeholder: "Focus: Offer testing", type: "textarea" },
      { key: "week_4", label: "Week 4", placeholder: "Focus: Scale what works", type: "textarea" }
    ]
  }
];

const VALIDATION_TESTS = [
  "Cold outreach to 10 potential customers",
  "Post in 3 relevant online communities",
  "Create a simple landing page with sign-up form",
  "Run a 48-hour pre-sale or waitlist campaign",
  "Conduct 5 customer discovery calls",
  "Build a no-code MVP prototype",
  "Send a value-first email to your network",
  "Test pricing with a fake door test",
  "Create and share a value-driven content piece",
  "Partner pitch to 3 complementary businesses"
];

export default function CanvasPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get("assessment");
  const { getAuthHeaders, user } = useAuth();
  const [currentSection, setCurrentSection] = useState(0);
  const [canvasName, setCanvasName] = useState("");
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [prefillInfo, setPrefillInfo] = useState(null);

  // Pre-fill from assessment if assessment ID is provided
  useEffect(() => {
    if (assessmentId && user) {
      fetchPrefillData();
    }
  }, [assessmentId, user]);

  const fetchPrefillData = async () => {
    setLoadingPrefill(true);
    try {
      const response = await axios.get(
        `${API}/canvas/prefill/${assessmentId}`,
        { headers: getAuthHeaders() }
      );
      const data = response.data;
      
      // Set canvas name from business name
      if (data.business_name) {
        setCanvasName(`${data.business_name} - Validation Canvas`);
      }
      
      // Set pre-filled form data
      if (data.prefilled_data) {
        setFormData(data.prefilled_data);
      }
      
      // Store prefill info for display
      setPrefillInfo({
        foundrscore: data.foundrscore,
        readiness_level: data.readiness_level,
        business_name: data.business_name,
        message: data.message
      });
      
      toast.success("Canvas pre-filled from your assessment!");
    } catch (error) {
      console.error("Failed to prefill canvas:", error);
      if (error.response?.status === 403) {
        toast.error("Pro subscription required for canvas pre-fill");
      } else {
        toast.error("Failed to load assessment data");
      }
    } finally {
      setLoadingPrefill(false);
    }
  };

  const handleFieldChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleArrayChange = (key, index, value) => {
    const current = formData[key] || ["", "", ""];
    const updated = [...current];
    updated[index] = value;
    setFormData(prev => ({ ...prev, [key]: updated }));
  };

  const handleChecklistChange = (key, test, checked) => {
    const current = formData[key] || [];
    const updated = checked 
      ? [...current, test]
      : current.filter(t => t !== test);
    setFormData(prev => ({ ...prev, [key]: updated }));
  };

  const handleNext = () => {
    if (currentSection < SECTIONS.length - 1) {
      setCurrentSection(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
    }
  };

  const handleSave = async () => {
    if (!canvasName.trim()) {
      toast.error("Please enter a name for your canvas");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/canvas/save`,
        { name: canvasName, sections: formData },
        { headers: getAuthHeaders() }
      );
      toast.success("Canvas saved successfully!");
      navigate(`/canvas/${response.data.canvas_id}`);
    } catch (error) {
      toast.error("Failed to save canvas");
    } finally {
      setSaving(false);
    }
  };

  const section = SECTIONS[currentSection];
  const Icon = section.icon;
  const progress = ((currentSection + 1) / SECTIONS.length) * 100;

  const renderField = (field) => {
    switch (field.type) {
      case "textarea":
        return (
          <Textarea
            id={field.key}
            value={formData[field.key] || ""}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className="min-h-[100px]"
            data-testid={`canvas-field-${field.key}`}
          />
        );
      case "array":
        return (
          <div className="space-y-2">
            {[0, 1, 2].map((index) => (
              <Input
                key={index}
                value={(formData[field.key] || [])[index] || ""}
                onChange={(e) => handleArrayChange(field.key, index, e.target.value)}
                placeholder={`${field.placeholder} #${index + 1}`}
                data-testid={`canvas-field-${field.key}-${index}`}
              />
            ))}
          </div>
        );
      case "checklist":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {VALIDATION_TESTS.map((test, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Checkbox
                  id={`test-${index}`}
                  checked={(formData[field.key] || []).includes(test)}
                  onCheckedChange={(checked) => handleChecklistChange(field.key, test, checked)}
                  data-testid={`canvas-test-${index}`}
                />
                <Label htmlFor={`test-${index}`} className="text-sm leading-tight cursor-pointer">
                  {test}
                </Label>
              </div>
            ))}
          </div>
        );
      default:
        return (
          <Input
            id={field.key}
            value={formData[field.key] || ""}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            data-testid={`canvas-field-${field.key}`}
          />
        );
    }
  };

  return (
    <div className="py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">One-Page Validation Canvas</h1>
          <p className="text-muted-foreground">
            Define your testable business idea in one page
          </p>
        </div>

        {/* Pre-fill Alert (if loaded from assessment) */}
        {prefillInfo && (
          <Alert className="mb-6 border-emerald-200 bg-emerald-50" data-testid="prefill-alert">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-emerald-800">
              <span className="font-semibold">Pre-filled from your assessment</span>
              {prefillInfo.business_name && (
                <span className="ml-1">({prefillInfo.business_name})</span>
              )}
              {prefillInfo.foundrscore && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-emerald-200">
                  FoundrScore: {prefillInfo.foundrscore}/100
                </span>
              )}
              <p className="text-sm mt-1">{prefillInfo.message}</p>
            </AlertDescription>
          </Alert>
        )}

        {/* Loading state for prefill */}
        {loadingPrefill && (
          <div className="flex items-center justify-center gap-2 mb-6 p-4 bg-muted/50 rounded-lg">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Loading assessment data...</span>
          </div>
        )}

        {/* Canvas Name */}
        <div className="mb-8">
          <Label htmlFor="canvas-name">Canvas Name</Label>
          <Input
            id="canvas-name"
            value={canvasName}
            onChange={(e) => setCanvasName(e.target.value)}
            placeholder="e.g., My SaaS Idea - v1"
            className="mt-1"
            data-testid="canvas-name-input"
          />
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium">Section {currentSection + 1} of {SECTIONS.length}</span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" data-testid="canvas-progress" />
        </div>

        {/* Section Card */}
        <Card className="mb-8" data-testid="canvas-section-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle>{section.title}</CardTitle>
                <CardDescription>{section.description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {section.fields.map((field) => (
              <div key={field.key}>
                <Label htmlFor={field.key} className="mb-2 block">{field.label}</Label>
                {renderField(field)}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentSection === 0}
            className="rounded-full"
            data-testid="canvas-prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {currentSection === SECTIONS.length - 1 ? (
            <Button
              onClick={handleSave}
              disabled={saving || !canvasName.trim()}
              className="rounded-full px-8"
              data-testid="canvas-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Save Canvas
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="rounded-full"
              data-testid="canvas-next-btn"
            >
              Next Section
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Section indicators */}
        <div className="flex justify-center gap-2 mt-8">
          {SECTIONS.map((s, index) => (
            <button
              key={s.id}
              onClick={() => setCurrentSection(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentSection
                  ? "bg-primary"
                  : index < currentSection
                  ? "bg-primary/40"
                  : "bg-muted"
              }`}
              data-testid={`canvas-indicator-${index}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
